/**
 * The deal summary shown for a tract on the deals map — in the hover and in
 * the click side panel. Pure.
 *
 * Field ORDER is fixed here (the landman's reading order); every LABEL comes
 * from the ui metadata of the table the field lives on. A field with no
 * metadata row falls back to its title-cased name and is reported in
 * `missingLabels` so the gap is visible rather than papered over.
 */
import { parseDateMs } from "./dateFormat";
import { fmtNumber, learnFieldFormats, titleCase } from "./metadataV2Columns";

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const blank = (v) => v === null || v === undefined || v === "";
const num = (v) => {
  if (blank(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Whole days since `value` (null when it can't be read). */
export const daysSince = (value, now = Date.now()) => {
  const ms = parseDateMs(value);
  if (ms == null || !Number.isFinite(ms)) return null;
  return Math.max(0, Math.floor((now - ms) / MS_PER_DAY));
};

/**
 * @param {object} row  joined tract row (dealTracts.joinTractRows)
 * @param {{ deals?: object[], lease_deeds?: object[],
 *   lease_deed_tracts?: object[] }} meta  metadata rows per table
 * @param {{ now?: number }} [options]
 * @returns {{ rows: Array<{field:string,label:string,text:string}>,
 *   missingLabels: string[] }}  missingLabels as "table.field"
 */
export const tractSummaryRows = (row, meta = {}, options = {}) => {
  const now = options.now ?? Date.now();
  const r = row || {};
  const rows = [];
  const missingLabels = [];
  for (const t of ["deals", "lease_deeds", "lease_deed_tracts"]) {
    learnFieldFormats(meta[t]);
  }

  const labelFor = (table, field) => {
    const m = (Array.isArray(meta[table]) ? meta[table] : []).find(
      (x) => x?.field === field
    );
    if (m?.label) return m.label;
    missingLabels.push(`${table}.${field}`);
    return titleCase(field);
  };
  const push = (table, field, text) => {
    if (blank(text)) return;
    rows.push({ field, label: labelFor(table, field), text: String(text) });
  };

  push("lease_deeds", "lessor_grantor", r.lessor_grantor);

  if (!blank(r.deal_status)) {
    const days = daysSince(r.status_chg_date, now);
    push(
      "deals",
      "deal_status",
      days == null ? r.deal_status : `${r.deal_status} · ${days} days in status`
    );
  }

  const tractAcres = num(r.co_net_acres_delivered);
  if (tractAcres != null) {
    push(
      "lease_deed_tracts",
      "co_net_acres_delivered",
      fmtNumber(tractAcres, "co_net_acres_delivered")
    );
  }
  const dealAcres = num(r.deal_net_acres_delivered);
  if (dealAcres != null && Number(r.deal_tract_count) > 1) {
    push(
      "deals",
      "deal_net_acres_delivered",
      fmtNumber(dealAcres, "deal_net_acres_delivered")
    );
  }

  const str = [r.section, r.township, r.range].filter((v) => !blank(v)).join("-");
  const legal = [str, r.quartering].filter((v) => !blank(v)).join(" ");
  push("lease_deed_tracts", "legalstr", legal);

  push("deals", "brokerage_id", r.broker);
  push("deals", "deal_type", r.deal_type);

  // Bonus: $ per net acre when the deal's total bonus and net acres allow it,
  // otherwise the deal total under its own (deal-total) label.
  const totalBonus = num(r.total_bonus);
  if (totalBonus != null && dealAcres != null && dealAcres > 0) {
    push("deals", "bonus_per_acre", fmtNumber(totalBonus / dealAcres, "bonus_per_acre"));
  } else if (totalBonus != null) {
    push("deals", "total_bonus", fmtNumber(totalBonus, "total_bonus"));
  }

  push("deals", "deal_key", r.deal_key);
  return { rows, missingLabels };
};

const ownerKey = (r) => String(r?.lessor_grantor ?? "").trim().toUpperCase();

/**
 * Summary for a map shape that holds SEVERAL deals (one aliquot, many
 * owners): counts, status mix, owner-de-duplicated acres and brokers. The
 * per-deal detail lives in the click side panel; a hover listing dozens of
 * deals is unreadable.
 *
 * Acres: each owner's undivided interest is added; competing deals for the
 * SAME owner (same lessor on this land) count once, at the largest acreage.
 * Rows with no lessor can't be matched and are added as-is.
 *
 * @param {object[]} rows  the shape's joined tract rows
 * @param {{ bucketOf: (status:any)=>string,
 *   buckets: Array<{key:string,label:string}>,
 *   isOpen: (row:object)=>boolean, maxBrokers?: number }} deps
 * @returns {{ dealCount:number, activeCount:number, ownerCount:number,
 *   netAcres:number, statusMix:string, brokers:string }}
 */
export const tractGroupSummary = (rows, { bucketOf, buckets, isOpen, maxBrokers = 2 }) => {
  const list = Array.isArray(rows) ? rows : [];
  const deals = new Map(); // deal_id -> first row
  for (const r of list) if (!deals.has(r?.deal_id)) deals.set(r?.deal_id, r);

  const acresByOwner = new Map();
  let unmatchedAcres = 0;
  for (const r of list) {
    const n = num(r?.co_net_acres_delivered);
    if (n == null) continue;
    const o = ownerKey(r);
    if (!o) unmatchedAcres += n;
    else acresByOwner.set(o, Math.max(acresByOwner.get(o) ?? 0, n));
  }
  let netAcres = unmatchedAcres;
  for (const v of acresByOwner.values()) netAcres += v;

  const perBucket = new Map();
  const perBroker = new Map();
  let activeCount = 0;
  const owners = new Set();
  for (const r of deals.values()) {
    const b = bucketOf(r?.deal_status);
    perBucket.set(b, (perBucket.get(b) ?? 0) + 1);
    if (isOpen(r)) activeCount += 1;
    const broker = r?.broker || "(no broker)";
    perBroker.set(broker, (perBroker.get(broker) ?? 0) + 1);
    const o = ownerKey(r);
    if (o) owners.add(o);
  }

  const statusMix = buckets
    .filter((b) => perBucket.get(b.key))
    .map((b) => `${b.label} ${perBucket.get(b.key)}`)
    .join(" · ");
  const ranked = [...perBroker.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const shown = ranked.slice(0, maxBrokers).map(([name, n]) => `${name} (${n})`);
  if (ranked.length > maxBrokers) shown.push(`+${ranked.length - maxBrokers} more`);

  return {
    dealCount: deals.size,
    activeCount,
    ownerCount: owners.size,
    netAcres,
    statusMix,
    brokers: shown.join(", "),
  };
};
